import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND_FONT_EMBED_CSS } from "@/lib/brandFonts";

const ORANGE = "#ff5a00";
const BLACK = "#000000";

/* Inject data-URI @font-face as a same-origin <style> so html-to-image embeds
   the real site fonts into exported PNGs (no fallback / no clipping). */
function useEmbeddedBrandFonts() {
  useEffect(() => {
    const id = "brand-embed-fonts";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = BRAND_FONT_EMBED_CSS;
    document.head.appendChild(style);
    if (document.fonts?.load) {
      document.fonts.load("800 64px Archivo");
    }
  }, []);
}

/* The exact top-left website logo lockup, sized as fractions of the canvas WIDTH
   so every asset scales identically and the wordmark always stays on ONE row. */
function Lockup({ width, dark = true, tagline = true }) {
  const fs = width * 0.075; // wordmark font-size
  const bar = Math.max(2, fs * (3 / 22)); // exact site-logo ratio (3px bar on 22px text)
  const gap = fs * (3 / 22);
  const tag = width * 0.0155;
  const textColor = dark ? "#ffffff" : "#050505";
  const barColor = dark ? ORANGE : "#050505";
  const tagColor = dark ? "rgba(255,255,255,0.8)" : "rgba(5,5,5,0.75)";
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1 }}>
      <span
        style={{
          fontFamily: "'Archivo', sans-serif",
          fontWeight: 800,
          letterSpacing: "-0.025em",
          color: textColor,
          fontSize: fs,
          whiteSpace: "nowrap",
        }}
      >
        Lahden Autokulma
      </span>
      <span style={{ height: bar, alignSelf: "stretch", background: barColor, marginTop: gap, marginBottom: tagline ? gap : 0, marginRight: fs * -0.0975 }} />
      {tagline && (
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            color: tagColor,
            fontSize: tag,
            whiteSpace: "nowrap",
          }}
        >
          Käytettyjen autojen erikoisliike
        </span>
      )}
    </div>
  );
}

/* Fill-mode lockup for the roof sign: measures the real rendered wordmark and
   scales it so the text fills ~92% of the canvas width (capped by height). */
function FillLockup({ width, height }) {
  const spanRef = useRef(null);
  const targetW = width * 0.92;
  const maxFs = height * 0.6;
  const [fs, setFs] = useState(maxFs);

  useEffect(() => {
    let alive = true;
    const fit = () => {
      const el = spanRef.current;
      if (!el || !alive) return;
      const w = el.offsetWidth;
      if (!w) return;
      let next = fs * (targetW / w);
      if (next > maxFs) next = maxFs;
      if (Math.abs(next - fs) > 0.5) setFs(next);
    };
    if (document.fonts?.ready) document.fonts.ready.then(() => requestAnimationFrame(fit));
    else fit();
    return () => { alive = false; };
  }, [fs, targetW, maxFs]);

  const bar = Math.max(3, fs * (3 / 22)); // exact site-logo ratio (3px bar on 22px text)
  const gap = fs * (3 / 22);
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1 }}>
      <span
        ref={spanRef}
        style={{
          fontFamily: "'Archivo', sans-serif",
          fontWeight: 800,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          fontSize: fs,
          whiteSpace: "nowrap",
        }}
      >
        Lahden Autokulma
      </span>
      <span style={{ height: bar, alignSelf: "stretch", background: ORANGE, marginTop: gap, marginRight: fs * -0.0975 }} />
    </div>
  );
}

function Sign({ title, width, height, filename, dark = true, tagline = true, fill = false, pixelRatio = 2, bg = null, bgOpacity = 0.55, bgGradient = false, bgPos = "center", previewMax = 560, children }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      await document.fonts.ready;
      // ensure background photos are fully loaded before rasterizing
      await Promise.all(
        [...ref.current.querySelectorAll("img")].map(
          (img) => img.complete || new Promise((r) => { img.onload = r; img.onerror = r; })
        )
      );
      const opts = { pixelRatio, cacheBust: true, fontEmbedCSS: BRAND_FONT_EMBED_CSS };
      await toPng(ref.current, opts);
      const dataUrl = await toPng(ref.current, opts);
      const a = document.createElement("a");
      a.download = filename;
      a.href = dataUrl;
      a.click();
    } finally {
      setBusy(false);
    }
  };

  const scale = Math.min(1, previewMax / width);

  return (
    <div className="border border-white/10 bg-[#0d0d0d]" data-testid={`asset-${filename}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div>
          <h3 className="font-display text-white text-lg">{title}</h3>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/50 mt-1">{width * pixelRatio}×{height * pixelRatio}px</p>
        </div>
        <button
          onClick={download}
          disabled={busy}
          data-testid={`download-${filename}`}
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
        >
          <Download size={15} />
          {busy ? "Ladataan…" : "Lataa PNG"}
        </button>
      </div>
      <div className="p-5 overflow-hidden flex justify-center">
        <div style={{ width: width * scale, height: height * scale }}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
            <div
              ref={ref}
              style={{
                width,
                height,
                position: "relative",
                overflow: "hidden",
                background: dark ? BLACK : ORANGE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {bg && (
                <>
                  <img
                    src={bg}
                    alt=""
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: bgPos }}
                  />
                  <div style={{ position: "absolute", inset: 0, background: `rgba(0,0,0,${bgOpacity})` }} />
                  {bgGradient && (
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(100deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.15) 100%)" }} />
                  )}
                </>
              )}
              <div style={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {children ?? (fill ? <FillLockup width={width} height={height} /> : <Lockup width={width} dark={dark} tagline={tagline} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MONO = "'JetBrains Mono', monospace";
const ARCHIVO = "'Archivo', sans-serif";

function SlideLockupCorner() {
  return (
    <div style={{ position: "absolute", top: 90, left: 100, zIndex: 2 }}>
      <Lockup width={560} dark tagline={false} />
    </div>
  );
}

/* Right-side, vertically centered lockup — matches the reference garage photo layout */
function SideLockup({ width = 1200 }) {
  return (
    <div style={{ position: "absolute", right: 90, top: "50%", transform: "translateY(-50%)", zIndex: 2 }}>
      <Lockup width={width} dark tagline={false} />
    </div>
  );
}

function SlideBody({ overline, children }) {
  return (
    <div style={{ position: "absolute", left: 100, right: 100, bottom: 100, zIndex: 2 }}>
      <p style={{ fontFamily: MONO, textTransform: "uppercase", letterSpacing: "0.3em", color: ORANGE, fontSize: 26, margin: 0, marginBottom: 28 }}>
        {overline}
      </p>
      {children}
    </div>
  );
}

export default function BrandKit() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEmbeddedBrandFonts();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  if (loading || !user)
    return <div className="pt-40 text-center font-mono text-white/40">Ladataan…</div>;

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-24 px-6 md:px-12" data-testid="brandkit-page">
      <div className="max-w-5xl mx-auto">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Brand Kit · yksityinen</p>
        <h1 className="font-display text-white text-4xl sm:text-5xl mb-3">Kylttigrafiikat</h1>
        <p className="text-white/60 text-sm max-w-2xl mb-12">
          Kaikki kyltit käyttävät sivuston tarkkaa logoa (Archivo + JetBrains Mono). Jokainen skaalautuu
          täysin samalla tavalla — teksti aina yhdellä rivillä. Klikkaa “Lataa PNG”.
        </p>

        <div className="space-y-8">
          <Sign title="iMac-taustakuva 2 — Talli (4K)" width={1920} height={1080} filename="lahden-autokulma-imac-taustakuva-talli-4k.png" bg="/brand/garage-rear.jpg" bgOpacity={0.15} pixelRatio={2}>
            <SideLockup width={1200} />
          </Sign>
          <Sign title="TV-slide 3 — Talli (4K)" width={1920} height={1080} filename="lahden-autokulma-tv-3-talli-4k.png" bg="/brand/garage-rear.jpg" bgOpacity={0.15} pixelRatio={2}>
            <SideLockup width={1200} />
          </Sign>
          <Sign title="iMac-taustakuva 4K (3840×2160)" width={1920} height={1080} filename="lahden-autokulma-imac-taustakuva-4k.png" bg="/hero-car.jpg" bgOpacity={0.45} bgPos="center" pixelRatio={2}>
            <Lockup width={1300} dark tagline={false} />
          </Sign>
          <Sign title="TV-slide 1 — Rahoitus (4K)" width={1920} height={1080} filename="lahden-autokulma-tv-1-rahoitus-4k.png" bg="/hero-car.jpg" bgOpacity={0.45} bgGradient bgPos="right center" pixelRatio={2}>
            <SlideLockupCorner />
            <SlideBody overline="Rahoitus">
              <h2 style={{ fontFamily: ARCHIVO, fontWeight: 800, letterSpacing: "-0.02em", color: "#ffffff", fontSize: 92, lineHeight: 1.02, margin: 0 }}>
                Rahoitus jopa<br />ilman käsirahaa
              </h2>
              <p style={{ fontFamily: MONO, color: "rgba(255,255,255,0.9)", fontSize: 34, margin: 0, marginTop: 34 }}>
                Korko <span style={{ color: ORANGE }}>5,9&nbsp;%</span> · jopa 96 kk maksuaikaa
              </p>
              <p style={{ fontFamily: MONO, textTransform: "uppercase", letterSpacing: "0.18em", color: "rgba(255,255,255,0.65)", fontSize: 24, margin: 0, marginTop: 24 }}>
                Kysy lisätietoja rahoituksesta myyjältä!
              </p>
            </SlideBody>
          </Sign>
          <Sign title="TV-slide 2 — 30 vuotta (4K)" width={1920} height={1080} filename="lahden-autokulma-tv-2-30v-4k.png" bg="/hero-car.jpg" bgOpacity={0.45} bgGradient bgPos="25% center" pixelRatio={2}>
            <SlideLockupCorner />
            <SlideBody overline="Vaihtoautojen erikoisliike">
              <h2 style={{ fontFamily: ARCHIVO, fontWeight: 800, letterSpacing: "-0.02em", color: "#ffffff", fontSize: 92, lineHeight: 1.02, margin: 0 }}>
                Autokauppaa<br /><span style={{ color: ORANGE }}>30 vuoden</span><br />kokemuksella.
              </h2>
            </SlideBody>
          </Sign>
          <Sign title="Ultraleveä mainoskyltti 4K" width={5760} height={1080} filename="lahden-autokulma-mainoskyltti-5760x1080.png" tagline={false} pixelRatio={1} />
          <Sign title="Kattokyltti (teksti täyttää kyltin)" width={2400} height={500} filename="lahden-autokulma-kattokyltti.png" fill />
          <Sign title="Iso ovikyltti (oven yläpuolelle)" width={2400} height={600} filename="lahden-autokulma-ovikyltti-iso.png" tagline={false} />
          <Sign title="Keskikokoinen kyltti" width={1600} height={450} filename="lahden-autokulma-kyltti-keski.png" tagline={false} />
          <Sign title="Pieni kyltti" width={1000} height={320} filename="lahden-autokulma-kyltti-pieni.png" tagline={false} />
          <Sign title="Leveä banneri" width={1920} height={500} filename="lahden-autokulma-banneri-levea.png" tagline={false} />
          <Sign title="Neliö (some)" width={1080} height={1080} filename="lahden-autokulma-nelio.png" tagline={false} />
        </div>
      </div>
    </div>
  );
}
